import { useEffect, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { useCookies } from 'react-cookie';
import { displayError } from '../helpers';
import { getPincodeAreas, getSiteRoute } from '../services/ConcreteEstimatorServices';
import './SiteLocationDistancePicker.css';

const CUSTOM_AREA_VALUE = '__custom__';

function loadLeaflet() {
  if (window.L) {
    return Promise.resolve(window.L);
  }
  return new Promise((resolve, reject) => {
    const cssId = 'leaflet-css-cdn';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load map library'));
    document.body.appendChild(script);
  });
}

function isValidPincode(value) {
  return /^[1-9][0-9]{5}$/.test(String(value || '').trim());
}

/**
 * Pincode → select / add locality → distance & ETA from plant.
 */
function SiteLocationDistancePicker({
  plantId,
  pincode,
  onPincodeChange,
  onDistanceApplied,
  siteAddress,
  onAddressChange,
}) {
  const [cookies] = useCookies(['myToken']);
  const [areas, setAreas] = useState([]);
  const [selectedArea, setSelectedArea] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [busy, setBusy] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapError, setMapError] = useState('');
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [pin, setPin] = useState(null);

  const pincodeReady = isValidPincode(pincode);
  const usingCustom = selectedArea === CUSTOM_AREA_VALUE;

  useEffect(() => {
    setSelectedArea('');
    setCustomLocation('');
    setAreas([]);
    setPin(null);
    if (!pincodeReady) {
      return undefined;
    }
    let cancelled = false;
    setLookingUp(true);
    getPincodeAreas(cookies, String(pincode).trim())
      .then((data) => {
        if (!cancelled) {
          setAreas(data.areas || []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          displayError(error.response?.data || error.message, 'Pincode lookup failed');
        }
      })
      .finally(() => {
        if (!cancelled) setLookingUp(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cookies, pincode, pincodeReady]);

  const resolveAreaQuery = () => {
    if (usingCustom) {
      const custom = customLocation.trim();
      if (!custom) return '';
      const pin = String(pincode || '').trim();
      return pin ? `${custom}, ${pin}, India` : `${custom}, India`;
    }
    const area = areas.find((item) => item.label === selectedArea);
    return area?.query || area?.label || selectedArea || '';
  };

  const resolveAddressLabel = (route, areaQuery) => {
    if (usingCustom && customLocation.trim()) {
      return customLocation.trim();
    }
    const area = areas.find((item) => item.label === selectedArea);
    if (area?.name) {
      return [area.name, area.district, pincode].filter(Boolean).join(', ');
    }
    return route?.site?.label || areaQuery || siteAddress || '';
  };

  const applyRoute = (route, areaQuery) => {
    if (onDistanceApplied) {
      onDistanceApplied({
        distance_one_way_km: String(route.distance_km ?? ''),
        distance_round_trip_km: String(route.distance_round_trip_km ?? ''),
        estimated_travel_minutes: route.duration_minutes,
        duration_text: route.duration_text,
        site_latitude: route.site?.lat,
        site_longitude: route.site?.lng,
      });
    }
    if (onAddressChange) {
      const nextAddress = resolveAddressLabel(route, areaQuery);
      if (nextAddress && (usingCustom || !String(siteAddress || '').trim())) {
        onAddressChange(nextAddress);
      }
    }
  };

  const computeRoute = async ({ areaQuery, siteLat, siteLng } = {}) => {
    if (!plantId) {
      displayError('Select a plant first.', 'Distance');
      return;
    }
    const pinCode = String(pincode || '').trim();
    const query = areaQuery || resolveAreaQuery();
    if (!siteLat && !query && !isValidPincode(pinCode)) {
      displayError('Enter pincode and select or add a location.', 'Distance');
      return;
    }
    if (!siteLat && !query && isValidPincode(pinCode)) {
      displayError('Select an area from the list, or choose “Add custom location”.', 'Distance');
      return;
    }
    setBusy(true);
    try {
      const route = await getSiteRoute(cookies, {
        plant_id: plantId,
        pincode: pinCode || undefined,
        area_query: query || undefined,
        site_lat: siteLat,
        site_lng: siteLng,
      });
      applyRoute(route, query);
      setPin({ lat: route.site.lat, lng: route.site.lng });
    } catch (error) {
      displayError(error.response?.data || error.message, 'Distance calculation failed');
    } finally {
      setBusy(false);
    }
  };

  const handleAreaSelect = (value) => {
    setSelectedArea(value);
    if (!value || value === CUSTOM_AREA_VALUE) {
      return;
    }
    const area = areas.find((item) => item.label === value);
    computeRoute({ areaQuery: area?.query || area?.label || value });
  };

  const openMap = async () => {
    if (!plantId) {
      displayError('Select a plant first.', 'Map');
      return;
    }
    setMapOpen(true);
    setMapError('');
    try {
      const L = await loadLeaflet();
      let start = pin;
      if (!start) {
        const pinCode = String(pincode || '').trim();
        const query = resolveAreaQuery();
        const route = await getSiteRoute(cookies, {
          plant_id: plantId,
          pincode: pinCode || undefined,
          area_query: query || undefined,
        });
        start = { lat: route.site.lat, lng: route.site.lng };
        setPin(start);
        applyRoute(route, query);
      }
      window.setTimeout(() => {
        if (!mapRef.current) return;
        if (mapInstanceRef.current) {
          mapInstanceRef.current.remove();
          mapInstanceRef.current = null;
        }
        const map = L.map(mapRef.current).setView([start.lat, start.lng], 14);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap',
          maxZoom: 19,
        }).addTo(map);
        const marker = L.marker([start.lat, start.lng], { draggable: true }).addTo(map);
        marker.bindPopup('Drag to exact site location').openPopup();
        marker.on('dragend', () => {
          const { lat, lng } = marker.getLatLng();
          setPin({ lat, lng });
        });
        map.on('click', (event) => {
          marker.setLatLng(event.latlng);
          setPin({ lat: event.latlng.lat, lng: event.latlng.lng });
        });
        mapInstanceRef.current = map;
        markerRef.current = marker;
      }, 50);
    } catch (error) {
      setMapError(error.message || 'Could not open map');
    }
  };

  const confirmMapPin = () => {
    if (!pin) return;
    computeRoute({
      siteLat: pin.lat,
      siteLng: pin.lng,
      areaQuery: resolveAreaQuery() || undefined,
    });
    setMapOpen(false);
  };

  useEffect(() => () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }
  }, []);

  return (
    <>
      <label htmlFor="site_pincode" className="form-group col-sm-4 text-right">Pincode</label>
      <input
        id="site_pincode"
        name="site_pincode"
        className="form-control col-sm-7"
        value={pincode}
        onChange={(event) => onPincodeChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
        maxLength={6}
        placeholder="6-digit site pincode"
      />
      <br />

      {pincodeReady ? (
        <>
          <label htmlFor="site_area_locality" className="form-group col-sm-4 text-right">Area / Locality</label>
          <div className="site-distance-area-wrap form-control col-sm-7 px-0 border-0 bg-transparent">
            <select
              id="site_area_locality"
              className="form-control browser-default custom-select"
              value={selectedArea}
              onChange={(event) => handleAreaSelect(event.target.value)}
              disabled={lookingUp}
            >
              <option value="">
                {lookingUp ? 'Loading areas for this pincode…' : 'Select location within pincode'}
              </option>
              {areas.map((area) => (
                <option key={area.label} value={area.label}>{area.label}</option>
              ))}
              <option value={CUSTOM_AREA_VALUE}>+ Add custom location / landmark</option>
            </select>

            {usingCustom ? (
              <div className="site-distance-custom mt-2">
                <input
                  type="text"
                  className="form-control"
                  value={customLocation}
                  onChange={(event) => setCustomLocation(event.target.value)}
                  placeholder="Type locality, landmark or area name"
                />
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  className="mt-2"
                  disabled={busy || !plantId || !customLocation.trim()}
                  onClick={() => computeRoute()}
                >
                  Use this location
                </Button>
              </div>
            ) : null}

            <div className="site-distance-buttons mt-2">
              <Button
                type="button"
                color="primary"
                size="sm"
                disabled={busy || lookingUp || !plantId || (!selectedArea && !usingCustom)}
                onClick={() => computeRoute()}
              >
                {busy ? 'Calculating…' : 'Get distance'}
              </Button>
              <Button
                type="button"
                color="secondary"
                size="sm"
                disabled={busy || !plantId}
                onClick={openMap}
              >
                Adjust on map
              </Button>
            </div>
          </div>
          <br />
        </>
      ) : null}

      {mapOpen ? (
        <div className="site-distance-map-overlay">
          <div className="site-distance-map-modal">
            <div className="site-distance-map-header">
              <h5>Adjust site location</h5>
              <button type="button" className="site-distance-map-close" onClick={() => setMapOpen(false)}>×</button>
            </div>
            <p className="site-distance-map-hint">
              Drag the pin or click the map to set the exact site within this pincode. Distance and ETA update from the plant.
            </p>
            {mapError ? <div className="text-danger mb-2">{mapError}</div> : null}
            <div ref={mapRef} className="site-distance-map-canvas" />
            <div className="site-distance-map-footer">
              <Button type="button" color="secondary" size="sm" onClick={() => setMapOpen(false)}>Cancel</Button>
              <Button type="button" color="primary" size="sm" disabled={!pin || busy} onClick={confirmMapPin}>
                Use this location
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default SiteLocationDistancePicker;
